import React, { useState } from "react";
import { useTranslation } from "../../hooks/useTranslation";
import { useSitemapChildren } from "../../hooks/useApi";
import { LoadingSpinner } from "../LoadingSpinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Map,
  ExternalLink,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Link as LinkIcon,
  Package,
  Eye,
} from "lucide-react";

const SitemapDetailsDialog = ({
  isOpen,
  onClose,
  source,
  botId,
  onDeleteAll,
  onReprocessSource,
  onDeleteSource,
  onViewProduct,
  processingStatuses = {},
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Determine the sitemap ID - if source has parent_sitemap_id, it's a URL from a sitemap
  // Otherwise, it's the sitemap itself
  // Handle both populated object and plain ID
  let sitemapId = null;
  if (source?.parent_sitemap_id) {
    // If parent_sitemap_id is an object (populated), extract _id
    sitemapId =
      typeof source.parent_sitemap_id === "object"
        ? source.parent_sitemap_id._id
        : source.parent_sitemap_id;
  } else {
    // Otherwise use the source's own ID
    sitemapId = source?._id;
  }

  const {
    data: childrenData,
    isLoading,
    error,
  } = useSitemapChildren(botId, sitemapId, {
    page: currentPage,
    limit: 10,
  });

  const children = childrenData?.children || [];
  const pagination = childrenData?.pagination || null;

  const getSourceDisplayName = (source) => {
    if (source.type === "url") {
      return source.metadata?.title || source.url;
    }
    return source.url || source.title || t("dialogs.sitemapDetails.untitled", "Untitled");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ready":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "processing":
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ready":
        return "bg-green-100 text-green-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "error":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (source) => {
    const type = typeof source === "object" ? source.type : source;
    const parentSitemapId =
      typeof source === "object" ? source.parent_sitemap_id : null;

    switch (type) {
      case "url":
        if (parentSitemapId) {
          return <Map className="h-4 w-4 text-green-600" />;
        }
        return <LinkIcon className="h-4 w-4 text-blue-600" />;
      default:
        return <LinkIcon className="h-4 w-4 text-blue-600" />;
    }
  };

  const formatLastRun = (timestamp) => {
    if (!timestamp) return t("dialogs.sitemapDetails.never", "Never");
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return t("dialogs.sitemapDetails.justNow", "Just now");
    if (diffMinutes < 60)
      return t("dialogs.sitemapDetails.minutesAgo", "{{count}}m ago", {
        count: diffMinutes,
      });

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)
      return t("dialogs.sitemapDetails.hoursAgo", "{{count}}h ago", {
        count: diffHours,
      });

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7)
      return t("dialogs.sitemapDetails.daysAgo", "{{count}}d ago", {
        count: diffDays,
      });

    return date.toLocaleDateString();
  };

  const getSourceSubtitle = (source) => {
    if (source.type === "url" && source.parent_sitemap_id) {
      const refreshText = source.refresh_days
        ? t("dialogs.sitemapDetails.refreshesEveryDays", "Refreshes every {{count}} days", {
            count: source.refresh_days,
          })
        : "";
      const parentText = t("dialogs.sitemapDetails.fromSitemap", "From sitemap");
      return refreshText ? `${parentText} • ${refreshText}` : parentText;
    }
    if (source.type === "url" && source.refresh_days) {
      return t("dialogs.sitemapDetails.refreshesEveryDays", "Refreshes every {{count}} days", {
        count: source.refresh_days,
      });
    }
    return source.type.toUpperCase();
  };

  const handleDeleteAll = async () => {
    // Use the correct sitemap ID (handle both populated and plain ID)
    let deleteId = null;
    if (source?.parent_sitemap_id) {
      deleteId =
        typeof source.parent_sitemap_id === "object"
          ? source.parent_sitemap_id._id
          : source.parent_sitemap_id;
    } else if (source?._id) {
      deleteId = source._id;
    }

    if (deleteId) {
      await onDeleteAll(deleteId);
    }
    setShowDeleteConfirm(false);
    onClose();
  };

  if (!source) return null;

  // Determine the sitemap URL - handle both populated object and plain values
  let sitemapUrl = null;
  if (source.type === "sitemap") {
    sitemapUrl = source.url;
  } else if (source.parent_sitemap_id) {
    // If parent_sitemap_id is an object (populated), use its url property
    sitemapUrl =
      typeof source.parent_sitemap_id === "object"
        ? source.parent_sitemap_id.url
        : source.parent_sitemap_url; // Fallback to direct field
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Map className="h-5 w-5 text-green-600" />
            <span>{t("dialogs.sitemapDetails.title", "Sitemap Details")}</span>
          </DialogTitle>
          <DialogDescription>
            {t("dialogs.sitemapDetails.description", "All URLs extracted from this sitemap")}
          </DialogDescription>
        </DialogHeader>

        {/* Sitemap URL */}
        <div className="border-b pb-4">
          <label className="text-sm font-medium text-muted-foreground">
            {t("dialogs.sitemapDetails.sitemapUrl", "Sitemap URL")}
          </label>
          <div className="mt-1 flex items-center space-x-2">
            <a
              href={sitemapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 underline break-all flex-1"
            >
              {sitemapUrl}
            </a>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" asChild>
              <a href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          </div>
        </div>

        {/* URLs List */}
        <div className="flex-1 overflow-y-auto space-y-3">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
              <span className="ml-3 text-sm text-muted-foreground">
                {t("dialogs.sitemapDetails.loadingUrls", "Loading URLs...")}
              </span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {t("dialogs.sitemapDetails.errorLoadingUrls", "Error loading URLs")}
                  </h3>
                  <p className="text-sm text-red-700 mt-1">{error.message}</p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && children.length === 0 && (
            <div className="text-center py-8">
              <Map className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-sm font-medium text-foreground">
                {t("dialogs.sitemapDetails.noUrlsFound", "No URLs found")}
              </h3>
              <p className="mt-2 text-xs text-muted-foreground">
                {t("dialogs.sitemapDetails.noUrlsYet", "This sitemap doesn't have any URLs yet.")}
              </p>
            </div>
          )}

          {!isLoading && children.length > 0 && (
            <>
              {children.map((child) => (
                <div
                  key={child._id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 mt-1">
                        {getTypeIcon(child)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h3
                            className="text-sm font-medium text-foreground truncate"
                            title={getSourceDisplayName(child)}
                          >
                            {getSourceDisplayName(child)}
                          </h3>

                          {/* Product badge for URL sources with product content */}
                          {child.type === "url" &&
                            child.content_type === "product" && (
                              <div className="flex items-center space-x-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                  <Package className="h-3 w-3 mr-1" />
                                  {t("dialogs.sitemapDetails.product", "Product")}
                                </span>
                              </div>
                            )}

                          {child.type === "url" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              asChild
                            >
                              <a
                                href={child.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={child.url}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                        {/* Show URL if metadata.title is being displayed */}
                        {child.type === "url" && child.metadata?.title && (
                          <p
                            className="text-xs text-muted-foreground mt-0.5 truncate"
                            title={child.url}
                          >
                            {child.url}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {getSourceSubtitle(child)}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(child.status)}
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                child.status
                              )}`}
                            >
                              {child.status}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {t("dialogs.sitemapDetails.lastRun", "Last run")}: {formatLastRun(child.last_run)}
                          </span>
                          {child.chunkCount && (
                            <span className="text-xs text-muted-foreground">
                              {t("dialogs.sitemapDetails.chunks", "{{count}} chunks", {
                                count: child.chunkCount,
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 sm:ml-4 mt-1 sm:mt-0">
                      {/* Eye button for product sources */}
                      {child.type === "url" &&
                        child.content_type === "product" &&
                        onViewProduct && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewProduct(child)}
                            title={t("dialogs.sitemapDetails.viewProductDetails", "View product details")}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                      {onReprocessSource && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onReprocessSource(child._id)}
                          disabled={
                            child.status === "processing" ||
                            child.status === "pending" ||
                            processingStatuses[child._id]
                          }
                        >
                          {processingStatuses[child._id] ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {onDeleteSource && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDeleteSource(child)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="border-t pt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {t("dialogs.sitemapDetails.pageOf", "Page {{current}} of {{total}}", {
                current: pagination.current_page,
                total: pagination.total_pages,
              })}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t("common.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === pagination.total_pages}
              >
                {t("common.next")}
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm ? (
          <div className="border-t pt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-red-800">
                    {t("dialogs.sitemapDetails.confirmDeletion", "Confirm Deletion")}
                  </h4>
                  <p className="text-sm text-red-700 mt-1">
                    {t(
                      "dialogs.sitemapDetails.deleteAllWarning",
                      "This will permanently delete the sitemap and all {{count}} associated URLs. This action cannot be undone.",
                      { count: pagination?.total_items || children.length }
                    )}
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleDeleteAll}>
                <Trash2 className="h-4 w-4 mr-2" />
                {t("dialogs.sitemapDetails.deleteAllUrls", "Delete All URLs")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {t("common.close")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("dialogs.sitemapDetails.deleteSitemap", "Delete Sitemap")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SitemapDetailsDialog;
